import { useMemo, useState } from "react";
import {
  Messages,
  NotificationSeverity,
  TABLE_ACTIONS,
} from "helpers/constants";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import CustomTable from "components/Table/CustomTable";
import { RequirementDomain } from "types/requirementDomain";
import {
  useDeleteRequirementDomainMutation,
  useGetRequirementDomainsQuery,
} from "graphql/__generated__/hooks";
import { MainLayoutContainer } from "components/Layout";
import { Add } from "@mui/icons-material";
import CreateOrEditDomainModal from "modals/CreateOrEditDomainModal/CreateOrEditDomainModal";
import { ResponsiveButton } from "components/Button";
import { useNotifications } from "hooks/useNotifications";
import { Box, Stack } from "@mui/material";
import { useParams } from "react-router-dom";

function normalizeHex(color?: string | null): string | null {
  if (!color) {
    return null;
  }
  const trimmed = color.trim();
  if (!trimmed) {
    return null;
  }
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(withHash) ? withHash : null;
}

export function DomainList() {
  const { organizationId } = useParams();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [domainIdToDelete, setDomainIdToDelete] = useState("");
  const [selectedDomain, setSelectedDomain] = useState<RequirementDomain | null>(
    null,
  );
  const [deleteDomain] = useDeleteRequirementDomainMutation();
  const { notify } = useNotifications();

  const where = useMemo(
    () =>
      organizationId
        ? { org_id: { _eq: organizationId } }
        : { org_id: { _is_null: true } },
    [organizationId],
  );

  const { loading, error, data, refetch } = useGetRequirementDomainsQuery({
    variables: { where },
    fetchPolicy: "network-only",
  });

  const domains = (data?.requirement_domain || []) as RequirementDomain[];
  const showColorColumn = domains.some((domain) =>
    Boolean(normalizeHex(domain.color_hex)),
  );

  const handleActionClick = (action: TABLE_ACTIONS, id: string) => {
    switch (action) {
      case TABLE_ACTIONS.EDIT: {
        const domainToEdit = domains.find(
          (domain) => domain.requirement_domain_id === id,
        );
        if (domainToEdit) {
          setSelectedDomain(domainToEdit);
          setCreateModalOpen(true);
        }
        break;
      }
      case TABLE_ACTIONS.DELETE:
        setDomainIdToDelete(id);
        setDeleteModalOpen(true);
        break;
      default:
        break;
    }
  };

  const handleSaved = () => {
    setSelectedDomain(null);
    setCreateModalOpen(false);
    refetch();
  };

  const handleDelete = async () => {
    if (!domainIdToDelete) {
      return;
    }
    try {
      await deleteDomain({ variables: { id: domainIdToDelete } });
      await refetch();
      setDomainIdToDelete("");
      setDeleteModalOpen(false);
      notify({
        message: Messages.successDomainDeleted,
        severity: NotificationSeverity.success,
      });
    } catch (err) {
      notify({
        message:
          err instanceof Error ? err.message : "Failed to delete domain.",
        severity: NotificationSeverity.error,
      });
    }
  };

  return (
    <MainLayoutContainer
      title="Domains"
      loading={loading}
      error={error}
      buttonActions={
        <ResponsiveButton
          onClick={() => {
            setSelectedDomain(null);
            setCreateModalOpen(true);
          }}
          icon={<Add />}
        >
          New Domain
        </ResponsiveButton>
      }
    >
      <CustomTable
        entities={domains}
        handleActionClick={handleActionClick}
        fieldToFilter={(row: RequirementDomain, filterText: string) =>
          (row.name || "").toLowerCase().includes(filterText.toLowerCase()) ||
          (row.code || "").toLowerCase().includes(filterText.toLowerCase()) ||
          (row.description || "")
            .toLowerCase()
            .includes(filterText.toLowerCase())
        }
        columns={[
          ...(showColorColumn
            ? [
                {
                  id: "color",
                  label: "Color",
                  value: (row: RequirementDomain) => {
                    const color = normalizeHex(row.color_hex);
                    if (!color) {
                      return "—";
                    }
                    return (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            bgcolor: color,
                            border: "1px solid rgba(0,0,0,0.2)",
                            borderRadius: "2px",
                          }}
                        />
                        <span>{color}</span>
                      </Stack>
                    );
                  },
                },
              ]
            : []),
          {
            id: "name",
            label: "Name",
            value: "name",
          },
          {
            id: "description",
            label: "Description",
            value: (row: RequirementDomain) => {
              const description = row.description || "";
              return description.length > 128
                ? `${description.slice(0, 128)}…`
                : description || "—";
            },
          },
          { id: "actions", value: "" },
        ]}
        isSelectable={false}
        allowedMenuItems={[TABLE_ACTIONS.EDIT, TABLE_ACTIONS.DELETE]}
        extraActionItems={[]}
        idColumn={"requirement_domain_id"}
        enableCardViewInSmallDevices
      />

      {createModalOpen && (
        <CreateOrEditDomainModal
          open={createModalOpen}
          domain={selectedDomain}
          orgId={organizationId || null}
          onSaved={handleSaved}
          onDismiss={() => {
            setCreateModalOpen(false);
            setSelectedDomain(null);
          }}
        />
      )}

      {deleteModalOpen && (
        <ConfirmActionModal
          title={"Heads up!"}
          message={"Are you sure you want to delete this domain?"}
          show={deleteModalOpen}
          onConfirm={handleDelete}
          onDismiss={() => {
            setDeleteModalOpen(false);
          }}
        />
      )}
    </MainLayoutContainer>
  );
}
